import React, { useState, useEffect } from 'react';
import './App.css';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';

import { Checkbox } from './components/ui/checkbox';
import { Upload, Camera, ChefHat, Clock, Users, Heart, Sparkles, TrendingUp } from 'lucide-react';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const navigate = useNavigate();
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [preferences, setPreferences] = useState({
    meal_type: '',
    cooking_expertise: '',
    dietary_preferences: [],
    cuisine_style: '',
    use_preferences: false  // Toggle for whether to use cooking preferences in text-based generation
  });
  const [recipes, setRecipes] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [activeRecipe, setActiveRecipe] = useState(0);
  const [showRecipeDatabase, setShowRecipeDatabase] = useState(false);
  const [databaseRecipes, setDatabaseRecipes] = useState([]);
  const [dbHasMore, setDbHasMore] = useState(false);
  const [dbLoadingMore, setDbLoadingMore] = useState(false);

  const [selectedFilters, setSelectedFilters] = useState({
    category: '',
    meal_type: '',
    search: ''
  });
  
  // Phase 3: Enhanced UX state
  const [userSession] = useState(() => `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [popularRecipes, setPopularRecipes] = useState([]);
  const [collections, setCollections] = useState([]);
  const [currentView, setCurrentView] = useState('ai-generator'); // 'ai-generator', 'database', 'favorites', 'collections'
  
  // Authentication state
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: ''
  });

  const MAX_IMAGES = 6;

  const handleImagesUpload = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const allowedExt = /\.(jpe?g|png|webp)$/i;
    const maxSize = 10 * 1024 * 1024; // 10MB
    const valid = [];
    for (const f of files) {
      if (!allowedTypes.includes(f.type) || !allowedExt.test(f.name) || f.size > maxSize) {
        toast.error('Unsupported format. Please upload JPG, PNG, or WEBP up to 10MB.');
        continue;
      }
      valid.push(f);
    }
    if (valid.length === 0) return;
    setSelectedImages(prev => {
      if (prev.length + valid.length > MAX_IMAGES) {
        toast.info(`You can upload up to ${MAX_IMAGES} photos.`);
      }
      return [...prev, ...valid].slice(0, MAX_IMAGES);
    });
    valid.forEach(f => {
      const reader = new FileReader(); // eslint-disable-line no-undef
      reader.onload = (e) => setImagePreviews(prev => [...prev, { url: e.target.result, name: f.name }].slice(0, MAX_IMAGES));
      reader.readAsDataURL(f);
    });
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleImagesUpload(e.dataTransfer.files);
    }
  };

  const handleDietaryChange = (value, checked) => {
    setPreferences(prev => ({
      ...prev,
      dietary_preferences: checked 
        ? [...prev.dietary_preferences, value]
        : prev.dietary_preferences.filter(item => item !== value)
    }));
  };

  const analyzeIngredients = async () => {
    if (selectedImages.length === 0) {
      toast.error('Please upload at least one image first');
      return;
    }

    if (!preferences.meal_type || !preferences.cooking_expertise || !preferences.cuisine_style) {
      toast.error('Please fill in all preferences');
      return;
    }

    setIsAnalyzing(true);
    
    try {
      const formData = new FormData();
      selectedImages.forEach(img => formData.append('images', img));
      formData.append('preferences', JSON.stringify(preferences));

      const response = await axios.post(`${API}/analyze-ingredients`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setRecipes(response.data);
      setActiveRecipe(0);
      
      if (response.data.can_cook) {
        toast.success('Recipes generated successfully! 🍳');
      } else {
        toast.warning('Hmm, I need more ingredients to work with! 😊');
      }
    } catch (error) {
      console.error('Error analyzing ingredients:', error);
      toast.error('Failed to analyze ingredients. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const resetForm = () => {
    setSelectedImages([]);
    setImagePreviews([]);
    setRecipes(null);
    setActiveRecipe(0);
    setShowRecipeDatabase(false);
  };

  const DB_PAGE_SIZE = 24;

  const fetchDatabaseRecipes = async (filters = {}, append = false) => {
    try {
      if (append) setDbLoadingMore(true);
      const skip = append ? databaseRecipes.length : 0;
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      queryParams.append('limit', DB_PAGE_SIZE);
      queryParams.append('skip', skip);

      const response = await axios.get(`${API}/recipes/database?${queryParams}`);
      const data = response.data;
      setDatabaseRecipes(prev => (append ? [...prev, ...data] : data));
      setDbHasMore(data.length === DB_PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching database recipes:', error);
      toast.error('Failed to fetch recipes');
    } finally {
      setDbLoadingMore(false);
    }
  };

  const loadMoreRecipes = () => {
    fetchDatabaseRecipes(selectedFilters, true);
  };



  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...selectedFilters, [filterType]: value };
    setSelectedFilters(newFilters);
    // Don't override collection results with database results
    if (currentView !== 'collection-view') {
      fetchDatabaseRecipes(newFilters);
    }
  };

  const toggleRecipeDatabase = () => {
    if (!showRecipeDatabase) {
      fetchDatabaseRecipes();
    }
    setShowRecipeDatabase(!showRecipeDatabase);
    setCurrentView('database');
  };

  // Phase 3: Enhanced UX functions
  const addToFavorites = async (recipeId, recipeName) => {
    try {
      const formData = new FormData();
      formData.append('user_session', userSession);
      formData.append('recipe_id', recipeId);
      
      const response = await axios.post(`${API}/favorites/add`, formData);
      
      if (response.data.is_favorite) {
        setFavorites(prev => [...prev, { recipe_id: recipeId, recipe_name: recipeName }]);
        toast.success('Added to favorites! ⭐');
      }
    } catch (error) {
      console.error('Error adding to favorites:', error);
      toast.error('Failed to add to favorites');
    }
  };

  const removeFromFavorites = async (recipeId) => {
    try {
      const formData = new FormData();
      formData.append('user_session', userSession);
      formData.append('recipe_id', recipeId);
      
      await axios.post(`${API}/favorites/remove`, formData);
      setFavorites(prev => prev.filter(fav => fav.recipe_id !== recipeId));
      toast.success('Removed from favorites');
    } catch (error) {
      console.error('Error removing from favorites:', error);
      toast.error('Failed to remove from favorites');
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites/${userSession}`);
      setFavorites(response.data.favorites);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const fetchPopularRecipes = async () => {
    try {
      const response = await axios.get(`${API}/stats/popular-recipes`);
      setPopularRecipes(response.data.popular_recipes);
    } catch (error) {
      console.error('Error fetching popular recipes:', error);
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/collections`);
      setCollections(response.data.collections);
    } catch (error) {
      console.error('Error fetching collections:', error);
    }
  };

  const isRecipeFavorite = (recipeId) => {
    return favorites.some(fav => fav.recipe_id === recipeId);
  };

  const switchView = (view) => {
    setCurrentView(view);
    setShowRecipeDatabase(view === 'database');
    setRecipes(null);
    
    if (view === 'favorites') fetchFavorites();
    if (view === 'popular') fetchPopularRecipes();
    if (view === 'collections') fetchCollections();
    if (view === 'database') fetchDatabaseRecipes();
    if (view === 'ai-generator') {
      // Reset to show AI generator form
      setSelectedImages([]);
      setImagePreviews([]);
      setRecipes(null);
    }
  };

  // Authentication functions
  const handleAuthSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation to block malformed / malicious input
    const emojiRegex = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}\u{FE00}-\u{FE0F}]/u;
    const scriptRegex = /<\s*script|<\s*\/|javascript:|on\w+\s*=/i;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const email = authForm.email.trim();
    if (!emailRegex.test(email) || scriptRegex.test(email) || emojiRegex.test(email)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    if (authForm.password.length < 6 || scriptRegex.test(authForm.password) || emojiRegex.test(authForm.password)) {
      toast.error('Password must be at least 6 characters with no scripts or emojis.');
      return;
    }
    if (authMode === 'register') {
      const name = authForm.full_name.trim();
      if (!/^[a-zA-Z][a-zA-Z\s.'-]{1,49}$/.test(name)) {
        toast.error('Please enter a valid name (letters only).');
        return;
      }
      if (authForm.phone && !/^[+\d][\d\s-]{6,15}$/.test(authForm.phone.trim())) {
        toast.error('Please enter a valid phone number.');
        return;
      }
    }

    try {
      if (authMode === 'register') {
        const response = await axios.post(`${API}/auth/register`, {
          email: authForm.email,
          password: authForm.password,
          full_name: authForm.full_name,
          phone: authForm.phone
        });
        
        setUser(response.data.user);
        setShowAuthModal(false);
        toast.success('Registration successful! Welcome! 🎉');
        
      } else {
        const response = await axios.post(`${API}/auth/login`, {
          email: authForm.email,
          password: authForm.password
        });
        
        setUser(response.data.user);
        setShowAuthModal(false);
        toast.success(`Welcome back, ${response.data.user.full_name}! 👋`);
      }
      
      // Reset form
      setAuthForm({ email: '', password: '', full_name: '', phone: '' });
      
    } catch (error) {
      console.error('Auth error:', error);
      if (error.response?.status === 429) {
        toast.error('Too many attempts. Please wait a few minutes and try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Authentication failed');
      }
    }
  };

  const handleLogout = () => {
    setUser(null);
    setFavorites([]);
    toast.success('Logged out successfully');
  };

  const generateTextBasedRecipe = async () => {
    if (!preferences.ingredients_text && !preferences.recipe_request) {
      toast.error('Please enter either ingredients you have or the recipe you want to make');
      return;
    }

    // Check if user wants to include preferences and if they're complete
    const usePreferences = preferences.use_preferences;
    if (usePreferences && (!preferences.meal_type || !preferences.cooking_expertise || !preferences.cuisine_style)) {
      toast.error('Please fill in your cooking preferences or uncheck "Use Cooking Preferences"');
      return;
    }

    setIsAnalyzing(true);
    
    try {
      // Create a text-based prompt for recipe generation
      const isIngredientBased = !!preferences.ingredients_text;
      const inputText = preferences.ingredients_text || preferences.recipe_request;
      
      // Prepare form data for the backend
      const formData = new FormData();
      formData.append('input_text', inputText);
      formData.append('input_type', isIngredientBased ? 'ingredients' : 'recipe_request');
      
      // Only include preferences if user chose to use them
      if (usePreferences) {
        formData.append('preferences', JSON.stringify({
          meal_type: preferences.meal_type,
          cooking_expertise: preferences.cooking_expertise,
          dietary_preferences: preferences.dietary_preferences,
          cuisine_style: preferences.cuisine_style
        }));
      }
      
      const response = await axios.post(`${API}/recipes/text-based`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000  // 60 second timeout for recipe generation
      });

      setRecipes(response.data);
      setActiveRecipe(0);
      
      if (response.data.can_cook) {
        toast.success('Recipe generated successfully! 🍳');
      } else {
        toast.warning('Let me try a different approach! 😊');
      }
    } catch (error) {
      console.error('Error generating text-based recipe:', error);
      if (error.response) {
        // Backend responded with error
        console.error('Backend error:', error.response.data);
        toast.error(`Server error: ${error.response.data.detail || 'Please try again'}`);
      } else if (error.request) {
        // Request was made but no response received
        console.error('Network error:', error.request);
        toast.error('Network error. Please check your connection and try again.');
      } else {
        // Something else happened
        console.error('Request error:', error.message);
        toast.error('Failed to generate recipe. Please try again.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Initialize collections on app load
  useEffect(() => {
    fetchCollections();
    if (user) {
      fetchFavorites();
    }
  }, [user]);

  const renderRecipe = (recipe) => {
    if (!recipe) return null;

    return (
      <Card className="h-full overflow-auto bg-white/90 backdrop-blur-xl border-gray-200 card-hover">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              🍛 {recipe.name}
            </CardTitle>
            <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold px-3 py-1 rounded-full">
              {recipe.difficulty}
            </Badge>
          </div>
          <CardDescription className="flex items-center gap-2 text-gray-700 font-medium">
            <Clock className="w-4 h-4" />
            ⏰ {recipe.cooking_time}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Nutritional Info */}
          <div className="bg-gradient-to-r from-green-100 to-teal-100 p-4 rounded-2xl border border-green-300">
            <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-green-600" />
              💪 Nutrition Facts
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex justify-between bg-white/70 p-2 rounded-lg">
                <span className="text-gray-700">🔥 Calories:</span>
                <span className="font-bold text-gray-800">{recipe.nutritional_info?.calories}</span>
              </div>
              <div className="flex justify-between bg-white/70 p-2 rounded-lg">
                <span className="text-gray-700">💪 Protein:</span>
                <span className="font-bold text-gray-800">{recipe.nutritional_info?.protein}</span>
              </div>
              <div className="flex justify-between bg-white/70 p-2 rounded-lg">
                <span className="text-gray-700">🌾 Carbs:</span>
                <span className="font-bold text-gray-800">{recipe.nutritional_info?.carbs}</span>
              </div>
              <div className="flex justify-between bg-white/70 p-2 rounded-lg">
                <span className="text-gray-700">🥑 Fat:</span>
                <span className="font-bold text-gray-800">{recipe.nutritional_info?.fat}</span>
              </div>
              <div className="flex justify-between bg-white/70 p-2 rounded-lg">
                <span className="text-gray-700">🌿 Fiber:</span>
                <span className="font-bold text-gray-800">{recipe.nutritional_info?.fiber}</span>
              </div>
              <div className="col-span-2 bg-white/70 p-2 rounded-lg">
                <span className="text-gray-700">🌈 Vitamins: </span>
                <span className="font-bold text-green-700">{recipe.nutritional_info?.vitamins}</span>
              </div>
            </div>
          </div>

          {/* Ingredients */}
          <div>
            <h4 className="font-bold text-gray-800 mb-3 text-lg">🛒 What You'll Need</h4>
            <ul className="space-y-2">
              {recipe.ingredients?.map((ingredient, index) => (
                <li key={index} className="flex items-center gap-3 text-gray-800 bg-white/70 p-2 rounded-lg">
                  <span className="w-3 h-3 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full"></span>
                  <span className="font-medium">{ingredient}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Instructions */}
          <div>
            <h4 className="font-bold text-gray-800 mb-3 text-lg">👩‍🍳 Step-by-Step Magic</h4>
            <ol className="space-y-4">
              {recipe.instructions?.map((instruction, index) => (
                <li key={index} className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm rounded-full flex items-center justify-center font-bold shadow-lg">
                    {index + 1}
                  </span>
                  <span className="text-gray-800 font-medium bg-white/70 p-3 rounded-lg flex-1">{instruction}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Chef Tips */}
          {recipe.chef_tips && (
            <div className="bg-gradient-to-r from-yellow-100 to-orange-100 p-4 rounded-2xl border border-yellow-300">
              <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2 text-lg">
                <Sparkles className="w-5 h-5 text-yellow-600" />
                ✨ Pro Chef Secrets
              </h4>
              <p className="text-gray-800 font-medium italic bg-white/70 p-3 rounded-lg">{recipe.chef_tips}</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-xl border-b border-gray-200 sticky top-0 z-50 shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 rounded-2xl flex items-center justify-center shadow-lg">
                <ChefHat className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold font-serif bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Indian MasterChef ✨
                </h1>
                <p className="text-sm text-gray-700 font-medium">AI-powered healthy recipes from your ingredients 🍛</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* User Authentication */}
              {user ? (
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-800">Welcome, {user.full_name}!</p>
                    <p className="text-xs text-gray-600">{user.email}</p>
                  </div>
                  <Button 
                    onClick={handleLogout}
                    variant="outline"
                    size="sm"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                  >
                    Logout
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={() => setShowAuthModal(true)}
                  className="bg-gradient-to-r from-green-500 to-teal-500 text-white hover:from-green-600 hover:to-teal-600"
                >
                  🔐 Login / Register
                </Button>
              )}
              
              <div className="flex gap-2">
              <Button 
                onClick={() => switchView('ai-generator')}
                variant={currentView === 'ai-generator' ? 'default' : 'outline'}
                className={`font-medium px-4 py-2 text-sm ${currentView === 'ai-generator' 
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' 
                  : 'border-purple-300 text-purple-700 hover:bg-purple-50 backdrop-blur-sm bg-white/80'}`}
              >
                🤖 AI Recipe Generator
              </Button>
              <Button 
                onClick={() => switchView('database')}
                variant={(currentView === 'database' || currentView === 'collection-view') ? 'default' : 'outline'}
                className={`font-medium px-4 py-2 text-sm ${(currentView === 'database' || currentView === 'collection-view') 
                  ? 'bg-purple-600 text-white' 
                  : 'border-purple-300 text-purple-700 hover:bg-purple-50 backdrop-blur-sm bg-white/80'}`}
              >
                📚 Recipe Database
              </Button>
              <Button 
                onClick={() => switchView('favorites')}
                variant={currentView === 'favorites' ? 'default' : 'outline'}
                className={`font-medium px-4 py-2 text-sm ${currentView === 'favorites' 
                  ? 'bg-purple-600 text-white' 
                  : 'border-purple-300 text-purple-700 hover:bg-purple-50 backdrop-blur-sm bg-white/80'}`}
              >
                ⭐ Favorites ({favorites.length})
              </Button>
              <Button 
                onClick={() => switchView('collections')}
                variant={currentView === 'collections' ? 'default' : 'outline'}
                className={`font-medium px-4 py-2 text-sm ${currentView === 'collections' 
                  ? 'bg-purple-600 text-white' 
                  : 'border-purple-300 text-purple-700 hover:bg-purple-50 backdrop-blur-sm bg-white/80'}`}
              >
                📊 Collections
              </Button>
              {currentView !== 'ai-generator' && (
                <Button 
                  onClick={resetForm} 
                  variant="outline" 
                  className="border-purple-300 text-purple-700 hover:bg-purple-50 backdrop-blur-sm bg-white/80 font-medium px-4 py-2 text-sm"
                >
                  ✨ Reset
                </Button>
              )}
            </div>
          </div>
        </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 pb-28 md:pb-8">
        {currentView === 'ai-generator' ? (
          // AI Recipe Generator View (Primary Feature)
          <div className="max-w-4xl mx-auto space-y-8">
            {!recipes ? (
              <>
                {/* Recipe Generation Options */}
                <Card className="bg-white/90 backdrop-blur-xl card-hover">
                  <CardHeader className="text-center">
                    <CardTitle className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                      🤖 AI Recipe Generator
                    </CardTitle>
                    <CardDescription className="text-gray-700 font-medium">
                      Generate recipes by uploading photos OR typing your ingredients/recipe preferences! ✨
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="photo" className="w-full">
                      <TabsList className="grid w-full grid-cols-2 bg-white/50 rounded-xl">
                        <TabsTrigger value="photo" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white font-medium rounded-lg">
                          📸 Photo Upload
                        </TabsTrigger>
                        <TabsTrigger value="text" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white font-medium rounded-lg">
                          ✍️ Text Input
                        </TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="photo" className="mt-6">
                        {/* Image Upload Section */}
                        <div
                          className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300 ${
                            dragActive 
                              ? 'border-cyan-400 bg-cyan-400/20 shadow-lg shadow-cyan-400/25' 
                              : 'border-purple-300/50 hover:border-pink-400/70 hover:bg-pink-400/10 hover:shadow-lg hover:shadow-pink-400/20'
                          }`}
                          onDragEnter={handleDrag}
                          onDragLeave={handleDrag}
                          onDragOver={handleDrag}
                          onDrop={handleDrop}
                        >
                          {imagePreviews.length > 0 ? (
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="photo-preview-grid">
                                {imagePreviews.map((p, i) => (
                                  <div key={i} className="relative group">
                                    <img
                                      src={p.url}
                                      alt={`Preview ${i + 1}`}
                                      className="w-full h-28 object-cover rounded-lg shadow-md"
                                    />
                                    <button
                                      type="button"
                                      data-testid={`remove-photo-${i}`}
                                      onClick={() => removeImage(i)}
                                      className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-black/80"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <p className="text-sm text-gray-600 font-medium">{selectedImages.length} photo(s) selected • add up to {MAX_IMAGES}</p>
                              <label className="inline-block cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  data-testid="add-more-photos-input"
                                  className="hidden"
                                  onChange={(e) => handleImagesUpload(e.target.files)}
                                />
                                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 text-sm font-medium">➕ Add more photos</span>
                              </label>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl flex items-center justify-center shadow-xl">
                                <Upload className="w-10 h-10 text-white animate-bounce" />
                              </div>
                              <div>
                                <p className="text-xl font-bold text-gray-800">
                                  Drop your ingredient photo here, or <span className="text-purple-600 underline decoration-wavy">browse</span> 📱
                                </p>
                                <p className="text-sm text-gray-600 mt-2 font-medium">
                                  JPG, PNG, WEBP up to 10MB • AI-powered ingredient detection 🤖
                                </p>
                              </div>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                data-testid="photo-upload-input"
                                onChange={(e) => handleImagesUpload(e.target.files)}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              />
                            </div>
                          )}
                        </div>
                      </TabsContent>
                      
                      <TabsContent value="text" className="mt-6">
                        {/* Text Input Section */}
                        <div className="space-y-6">
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-200">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                              ✍️ Tell Me What You Want to Cook
                            </h3>
                            <div className="space-y-4">
                              <div>
                                <label className="text-sm font-medium text-gray-800 mb-2 block">
                                  Option 1: Ingredients You Have
                                </label>
                                <textarea
                                  className="w-full h-20 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                                  placeholder="e.g., I have chicken, onions, tomatoes, rice, and basic spices..."
                                  maxLength={1000}
                                  data-testid="ingredients-text-input"
                                  value={preferences.ingredients_text || ''}
                                  onChange={(e) => setPreferences(prev => ({...prev, ingredients_text: e.target.value, recipe_request: ''}))}
                                />
                              </div>
                              
                              <div className="text-center text-gray-500 font-medium">OR</div>
                              
                              <div>
                                <label className="text-sm font-medium text-gray-800 mb-2 block">
                                  Option 2: Recipe You Want to Make
                                </label>
                                <textarea
                                  className="w-full h-20 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                                  placeholder="e.g., I want to make authentic Hyderabadi biryani, or healthy South Indian breakfast..."
                                  maxLength={1000}
                                  data-testid="recipe-request-input"
                                  value={preferences.recipe_request || ''}
                                  onChange={(e) => setPreferences(prev => ({...prev, recipe_request: e.target.value, ingredients_text: ''}))}
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Preferences Toggle */}
                          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-200">
                            <div className="flex items-center space-x-3">
                              <Checkbox
                                id="use_preferences"
                                checked={preferences.use_preferences}
                                onCheckedChange={(checked) => setPreferences(prev => ({...prev, use_preferences: checked}))}
                              />
                              <label htmlFor="use_preferences" className="text-sm font-medium text-gray-800 cursor-pointer">
                                🎯 Use my cooking preferences below (optional)
                              </label>
                            </div>
                            <p className="text-xs text-gray-600 mt-2 ml-6">
                              ✨ Uncheck this to get quick recipes without needing to fill preferences
                            </p>
                          </div>
                          
                          {(!preferences.ingredients_text && !preferences.recipe_request) && (
                            <p className="text-xs text-red-500 font-medium text-center" data-testid="text-empty-warning">
                              Please enter ingredients you have or a dish you want to make.
                            </p>
                          )}
                          {(preferences.ingredients_text || preferences.recipe_request) && preferences.use_preferences && (!preferences.meal_type || !preferences.cooking_expertise || !preferences.cuisine_style) && (
                            <p className="text-xs text-red-500 font-medium text-center" data-testid="text-prefs-warning">
                              Please complete your cooking preferences below, or uncheck "Use my cooking preferences".
                            </p>
                          )}
                          <Button 
                            onClick={generateTextBasedRecipe}
                            data-testid="generate-text-recipe-btn"
                            disabled={isAnalyzing || (!preferences.ingredients_text && !preferences.recipe_request) || (preferences.use_preferences && (!preferences.meal_type || !preferences.cooking_expertise || !preferences.cuisine_style))}
                            className="w-full btn-gradient text-white py-4 text-xl font-bold rounded-2xl shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isAnalyzing ? (
                              <div className="flex items-center gap-3">
                                <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                                ✨ AI Creating Your Recipe...
                              </div>
                            ) : (
                              <div className="flex items-center gap-3">
                                <Sparkles className="w-6 h-6" />
                                🚀 Generate Recipe from Text
                              </div>
                            )}
                          </Button>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>

                {/* Preferences Section */}
                <Card className="bg-white/90 backdrop-blur-xl card-hover">
                  <CardHeader>
                    <CardTitle className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-purple-600 bg-clip-text text-transparent">
                      🎯 Your Cooking Preferences
                    </CardTitle>
                    <CardDescription className="text-gray-700 font-medium">
                      {currentView === 'home' && preferences.use_preferences ? 
                        "Fill these in for personalized recipes! 💫" :
                        "Tell me your cooking style so I can customize the perfect recipe for you! 💫"
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-800">Meal Type</label>
                        <select 
                          className="flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          value={preferences.meal_type}
                          onChange={(e) => setPreferences(prev => ({...prev, meal_type: e.target.value}))}
                        >
                          <option value="">Select meal type</option>
                          <option value="breakfast">Breakfast</option>
                          <option value="lunch">Lunch</option>
                          <option value="dinner">Dinner</option>
                          <option value="snack">Snack</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-800">Cooking Expertise</label>
                        <select 
                          className="flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          value={preferences.cooking_expertise}
                          onChange={(e) => setPreferences(prev => ({...prev, cooking_expertise: e.target.value}))}
                        >
                          <option value="">Select your level</option>
                          <option value="beginner">Beginner</option>
                          <option value="intermediate">Intermediate</option>
                          <option value="expert">Expert</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-800">Cuisine Style</label>
                        <select 
                          className="flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          value={preferences.cuisine_style}
                          onChange={(e) => setPreferences(prev => ({...prev, cuisine_style: e.target.value}))}
                        >
                          <option value="">Select style</option>
                          <option value="traditional">Traditional Indian</option>
                          <option value="fusion">Modern Fusion</option>
                          <option value="both">Both Styles</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="text-sm font-medium text-gray-800">Dietary Preferences</label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {['vegetarian', 'gluten-free', 'keto', 'low-carb', 'high-protein', 'dairy-free', 'nut-free'].map((diet) => (
                          <div key={diet} className="flex items-center space-x-2">
                            <Checkbox
                              id={diet}
                              onCheckedChange={(checked) => handleDietaryChange(diet, checked)}
                            />
                            <label htmlFor={diet} className="text-sm text-gray-700 capitalize font-medium">
                              {diet.replace('-', ' ')}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button 
                      onClick={analyzeIngredients}
                      data-testid="analyze-ingredients-btn"
                      disabled={isAnalyzing || selectedImages.length === 0}
                      className="w-full btn-gradient text-white py-4 text-xl font-bold rounded-2xl shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isAnalyzing ? (
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                          ✨ AI Magic in Progress...
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <Camera className="w-6 h-6" />
                          🚀 Generate Amazing Recipes
                        </div>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </>
            ) : (
              // Recipe Results Display
              <div className="max-w-7xl mx-auto space-y-6">
                {/* Identified Ingredients */}
                <Card className="bg-white/90 border-teal-300/50 backdrop-blur-xl card-hover">
                  <CardHeader>
                    <CardTitle className="text-2xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent flex items-center gap-2">
                      <Camera className="w-6 h-6 text-teal-600" />
                      🔍 AI Vision Results
                    </CardTitle>
                    <CardDescription className="text-gray-700 font-medium">
                      Check out what our AI spotted in your pic - we're about to create some fire recipes! 🔥
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-3">
                        {recipes.ingredients_identified && recipes.ingredients_identified.length > 0 ? (
                          recipes.ingredients_identified.map((ingredient, index) => (
                            <Badge key={index} className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-4 py-2 text-sm font-bold rounded-full shadow-lg">
                              ✅ {ingredient}
                            </Badge>
                          ))
                        ) : (
                          <Badge className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-4 py-2 rounded-full">
                            📸 AI processing your image...
                          </Badge>
                        )}
                      </div>
                      <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-4 rounded-2xl border border-purple-300">
                        <p className="text-sm text-gray-800 font-medium flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-yellow-600" />
                          <strong className="text-purple-700">Bonus Kitchen Essentials:</strong> <span className="text-gray-700">We're assuming you've got the Indian spice game covered! 🧄🌶️ (turmeric, cumin, coriander, garam masala, onions, tomatoes, ginger-garlic paste, oil)</span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Database Recipe Matches */}
                {recipes.database_recipes && recipes.database_recipes.length > 0 && (
                  <Card className="bg-white/90 border-orange-300/50 backdrop-blur-xl card-hover">
                    <CardHeader>
                      <CardTitle className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent flex items-center gap-2">
                        <ChefHat className="w-6 h-6 text-orange-600" />
                        👨‍🍳 Chef-Verified Recipes
                      </CardTitle>
                      <CardDescription className="text-gray-700 font-medium">
                        Found {recipes.total_database_matches} authentic recipes from renowned chefs using your ingredients!
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {recipes.database_recipes.slice(0, 4).map((dbRecipe, index) => (
                          <div key={index} className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-xl border border-orange-200">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-bold text-gray-800">{dbRecipe.name}</h4>
                              {dbRecipe.is_featured && (
                                <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs">
                                  ⭐
                                </Badge>
                              )}
                            </div>
                            <div className="space-y-2 text-sm">
                              <p className="text-orange-700">
                                <strong>Chef:</strong> {dbRecipe.chef_name}
                              </p>
                              <p className="text-gray-600">
                                <strong>Region:</strong> {dbRecipe.region} • <strong>Time:</strong> {dbRecipe.cooking_time}
                              </p>
                              {dbRecipe.matched_ingredients && (
                                <p className="text-green-700">
                                  <strong>Matches:</strong> {dbRecipe.matched_ingredients.join(', ')}
                                </p>
                              )}
                              <Button 
                                size="sm"
                                className="w-full mt-2 bg-gradient-to-r from-orange-500 to-red-500 text-white"
                                onClick={() => {
                                  setRecipes({
                                    ...recipes,
                                    main_recipe: {
                                      name: dbRecipe.name,
                                      ingredients: dbRecipe.ingredients,
                                      instructions: dbRecipe.instructions,
                                      cooking_time: dbRecipe.cooking_time,
                                      difficulty: dbRecipe.difficulty,
                                      nutritional_info: dbRecipe.nutritional_info,
                                      chef_tips: dbRecipe.chef_tips
                                    },
                                    chef_message: `This authentic ${dbRecipe.region} recipe is from renowned chef ${dbRecipe.chef_name}!`
                                  });
                                }}
                              >
                                Use This Recipe
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Chef Message */}
                <Card className="bg-white/90 border-pink-300/50 backdrop-blur-xl card-hover">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                        <ChefHat className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent mb-2">
                          👨‍🍳 Chef's Message
                        </h3>
                        <p className="text-gray-800 font-medium text-lg italic">{recipes.chef_message}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recipe Tabs */}
                <Tabs value={activeRecipe.toString()} onValueChange={(value) => setActiveRecipe(parseInt(value))}>
                  <TabsList className="grid w-full grid-cols-3 bg-white/90 backdrop-blur-xl rounded-2xl p-2">
                    <TabsTrigger 
                      value="0" 
                      className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-500 data-[state=active]:text-white text-gray-700 font-bold rounded-xl py-3 data-[state=active]:shadow-lg"
                    >
                      🌟 Main Recipe
                    </TabsTrigger>
                    <TabsTrigger 
                      value="1" 
                      className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-blue-500 data-[state=active]:text-white text-gray-700 font-bold rounded-xl py-3 data-[state=active]:shadow-lg"
                    >
                      🎨 Alternative 1
                    </TabsTrigger>
                    <TabsTrigger 
                      value="2" 
                      className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-teal-500 data-[state=active]:text-white text-gray-700 font-bold rounded-xl py-3 data-[state=active]:shadow-lg"
                    >
                      ✨ Alternative 2
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="0" className="mt-6">
                    {renderRecipe(recipes.main_recipe)}
                  </TabsContent>
                  
                  <TabsContent value="1" className="mt-6">
                    {renderRecipe(recipes.alternative_recipes?.[0])}
                  </TabsContent>
                  
                  <TabsContent value="2" className="mt-6">
                    {renderRecipe(recipes.alternative_recipes?.[1])}
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>
        ) : currentView === 'favorites' ? (
          <div className="max-w-6xl mx-auto space-y-6">
            <Card className="bg-white/90 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent text-center">
                  ⭐ My Favorite Recipes
                </CardTitle>
                <CardDescription className="text-center text-gray-700">
                  Your personal collection of loved recipes
                </CardDescription>
              </CardHeader>
            </Card>

            {favorites.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favorites.map((favorite, index) => (
                  <Card key={favorite.recipe_id} className="bg-white/90 backdrop-blur-xl hover:shadow-lg transition-all duration-300">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-lg text-gray-800">{favorite.recipe_name}</CardTitle>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => removeFromFavorites(favorite.recipe_id)}
                        >
                          ❤️
                        </Button>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Badge variant="outline" className="text-purple-600 border-purple-300">
                          👨‍🍳 {favorite.chef_name}
                        </Badge>
                        <Badge variant="outline" className="text-green-600 border-green-300">
                          📍 {favorite.region.charAt(0).toUpperCase() + favorite.region.slice(1)}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Button 
                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                        onClick={async () => {
                          // Fetch full recipe details
                          try {
                            const response = await axios.get(`${API}/recipes/database/${favorite.recipe_id}`);
                            const recipe = response.data;
                            setRecipes({
                              can_cook: true,
                              main_recipe: {
                                name: recipe.name,
                                ingredients: recipe.ingredients,
                                instructions: recipe.instructions,
                                cooking_time: recipe.cooking_time,
                                difficulty: recipe.difficulty,
                                nutritional_info: recipe.nutritional_info,
                                chef_tips: recipe.chef_tips
                              },
                              alternative_recipes: [],
                              chef_message: `This is one of your favorite recipes from renowned chef ${recipe.chef_name}!`,
                              ingredients_identified: recipe.primary_ingredients
                            });
                            setCurrentView('home');
                          } catch (error) {
                            console.error('Error fetching recipe details:', error);
                            toast.error('Failed to load recipe');
                          }
                        }}
                      >
                        View Recipe
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-white/90 backdrop-blur-xl">
                <CardContent className="pt-6 text-center py-12">
                  <div className="space-y-4">
                    <div className="text-6xl">⭐</div>
                    <h3 className="text-xl font-semibold text-gray-800">No Favorites Yet</h3>
                    <p className="text-gray-600">Start exploring recipes and add your favorites!</p>
                    <Button 
                      onClick={() => switchView('database')}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                    >
                      Browse Recipes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : currentView === 'popular' ? (
          <div className="max-w-6xl mx-auto space-y-6">
            <Card className="bg-white/90 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent text-center">
                  🔥 Popular Recipes
                </CardTitle>
                <CardDescription className="text-center text-gray-700">
                  Most loved recipes by our community
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {popularRecipes.map((recipe, index) => (
                <Card key={recipe.id} className="bg-white/90 backdrop-blur-xl hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg text-gray-800 line-clamp-2">{recipe.name}</CardTitle>
                      <div className="flex flex-col items-end gap-1">
                        {recipe.is_featured && (
                          <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs">
                            ⭐ Featured
                          </Badge>
                        )}
                        <Badge className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs">
                          ❤️ {recipe.favorite_count || 0}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline" className="text-purple-600 border-purple-300">
                        👨‍🍳 {recipe.chef_name}
                      </Badge>
                      <Badge variant="outline" className="text-green-600 border-green-300">
                        📍 {recipe.region.charAt(0).toUpperCase() + recipe.region.slice(1)}
                      </Badge>
                    </div>
                    {recipe.average_rating > 0 && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span>⭐ {recipe.average_rating}</span>
                        <span>({recipe.total_ratings} reviews)</span>
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>⏰ {recipe.cooking_time}</span>
                        <span>📊 {recipe.difficulty}</span>
                      </div>
                      <Button 
                        className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600"
                        onClick={() => {
                          setRecipes({
                            can_cook: true,
                            main_recipe: {
                              name: recipe.name,
                              ingredients: recipe.ingredients,
                              instructions: recipe.instructions,
                              cooking_time: recipe.cooking_time,
                              difficulty: recipe.difficulty,
                              nutritional_info: recipe.nutritional_info,
                              chef_tips: recipe.chef_tips
                            },
                            alternative_recipes: [],
                            chef_message: `This popular recipe from chef ${recipe.chef_name} is loved by ${recipe.favorite_count || 0} users!`,
                            ingredients_identified: recipe.primary_ingredients
                          });
                          setCurrentView('home');
                        }}
                      >
                        Try This Popular Recipe
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ) : (currentView === 'database' || currentView === 'collection-view') ? (
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Recipe Database Header */}
            <Card className="bg-white/90 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent text-center">
                  🍛 Chef's Recipe Collection
                </CardTitle>
                <CardDescription className="text-center text-gray-700">
                  Discover authentic recipes from India's most celebrated chefs
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Filters */}
            <Card className="bg-white/90 backdrop-blur-xl">
              <CardContent className="pt-6">
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-800 mb-2 block">🔍 Search Recipes</label>
                  <input
                    type="text"
                    data-testid="recipe-search-input"
                    className="w-full h-11 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Search by name or ingredient, e.g. paneer, biryani, dosa..."
                    value={selectedFilters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-800 mb-2 block">Category</label>
                    <select 
                      className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                      value={selectedFilters.category}
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                    >
                      <option value="">All Categories</option>
                      <option value="vegetarian">Vegetarian</option>
                      <option value="non-vegetarian">Non-Vegetarian</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-800 mb-2 block">Meal Type</label>
                    <select 
                      className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                      value={selectedFilters.meal_type}
                      onChange={(e) => handleFilterChange('meal_type', e.target.value)}
                    >
                      <option value="">All Meals</option>
                      <option value="breakfast">Breakfast</option>
                      <option value="lunch">Lunch</option>
                      <option value="dinner">Dinner</option>
                      <option value="snack">Snack</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recipe Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {databaseRecipes.map((recipe, index) => (
                <Card key={`${recipe.id}-${index}`} data-testid={`recipe-card-${recipe.id}`} className="bg-white/90 backdrop-blur-xl hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  <div
                    className="relative h-44 w-full overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/recipe/${recipe.id}`)}
                  >
                    <img
                      src={recipe.image_url || 'https://images.pexels.com/photos/35041652/pexels-photo-35041652.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'}
                      alt={recipe.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => { e.target.src = 'https://images.pexels.com/photos/35041652/pexels-photo-35041652.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'; }}
                    />
                    <div className="absolute top-2 right-2 flex gap-2 items-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        className={`p-1 h-8 w-8 rounded-full bg-white/80 backdrop-blur ${isRecipeFavorite(recipe.id) ? 'text-red-500' : 'text-gray-500 hover:text-red-500'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isRecipeFavorite(recipe.id)) {
                            removeFromFavorites(recipe.id);
                          } else {
                            addToFavorites(recipe.id, recipe.name);
                          }
                        }}
                      >
                        {isRecipeFavorite(recipe.id) ? '❤️' : '🤍'}
                      </Button>
                    </div>
                    {recipe.is_featured && (
                      <Badge className="absolute top-2 left-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
                        ⭐ Featured
                      </Badge>
                    )}
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle
                      className="text-lg text-gray-800 line-clamp-2 cursor-pointer hover:text-purple-600"
                      onClick={() => navigate(`/recipe/${recipe.id}`)}
                    >
                      {recipe.name}
                    </CardTitle>
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline" className="text-purple-600 border-purple-300">
                        👨‍🍳 {recipe.chef_name}
                      </Badge>
                      <Badge variant="outline" className="text-green-600 border-green-300">
                        📍 {recipe.region.charAt(0).toUpperCase() + recipe.region.slice(1)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>⏰ {recipe.cooking_time}</span>
                        <span>📊 {recipe.difficulty}</span>
                      </div>
                      <div className="text-sm text-gray-700">
                        <strong>Primary Ingredients:</strong> {recipe.primary_ingredients.slice(0, 3).join(', ')}
                        {recipe.primary_ingredients.length > 3 && '...'}
                      </div>
                      <Button 
                        data-testid={`view-recipe-${recipe.id}`}
                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                        onClick={() => navigate(`/recipe/${recipe.id}`)}
                      >
                        View Full Recipe
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {currentView === 'database' && dbHasMore && databaseRecipes.length > 0 && (
              <div className="flex justify-center pt-4">
                <Button
                  onClick={loadMoreRecipes}
                  disabled={dbLoadingMore}
                  data-testid="load-more-recipes-btn"
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 px-8 py-3 rounded-2xl font-semibold disabled:opacity-50"
                >
                  {dbLoadingMore ? 'Loading...' : 'Load More Recipes'}
                </Button>
              </div>
            )}

            {databaseRecipes.length === 0 && (
              <Card className="bg-white/90 backdrop-blur-xl">
                <CardContent className="pt-6 text-center">
                  <p className="text-gray-600">No recipes found matching your filters. Try adjusting your search criteria!</p>
                </CardContent>
              </Card>
            )}
          </div>
        ) : currentView === 'collections' ? (
          <div className="max-w-6xl mx-auto space-y-8">
            {/* Collections Section */}
            <Card className="bg-white/90 backdrop-blur-xl">
              <CardHeader>
                <CardTitle className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent text-center">
                  📊 Recipe Collections
                </CardTitle>
                <CardDescription className="text-center text-gray-700">
                  Discover curated collections of authentic Indian recipes
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {collections.map((collection, index) => (
                <Card key={collection.id} className="bg-white/90 backdrop-blur-xl hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-xl text-gray-800">{collection.name}</CardTitle>
                      {collection.is_featured && (
                        <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
                          ⭐ Featured
                        </Badge>
                      )}
                    </div>
                    <CardDescription className="text-gray-600">
                      {collection.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">
                          📚 {collection.recipe_count} recipes
                        </span>
                        <Button 
                          className="bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                          onClick={async () => {
                            try {
                              // Clear any existing filters
                              setSelectedFilters({ category: '', meal_type: '' });
                              
                              // Fetch recipes for this specific collection
                              const response = await axios.get(`${API}/collections/${collection.id}/recipes`);
                              setDatabaseRecipes(response.data);
                              // Switch to database view but prevent automatic database fetching
                              setCurrentView('collection-view');
                              setShowRecipeDatabase(true);
                              setRecipes(null);
                              toast.success(`Showing ${response.data.length} recipes from ${collection.name}`);
                            } catch (error) {
                              console.error('Error fetching collection recipes:', error);
                              toast.error('Failed to load collection recipes');
                            }
                          }}
                        >
                          Explore Collection
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-gradient-to-r from-green-100 to-teal-100 border-green-200">
                <CardContent className="pt-6 text-center">
                  <div className="space-y-4">
                    <div className="text-4xl">🤖</div>
                    <h3 className="text-xl font-semibold text-gray-800">AI Recipe Generator</h3>
                    <p className="text-gray-600">Upload a photo of your ingredients and get personalized recipes!</p>
                    <Button 
                      onClick={() => switchView('ai-generator')}
                      className="bg-gradient-to-r from-green-500 to-teal-500 text-white"
                    >
                      Try AI Generator
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-r from-orange-100 to-red-100 border-orange-200">
                <CardContent className="pt-6 text-center">
                  <div className="space-y-4">
                    <div className="text-4xl">👨‍🍳</div>
                    <h3 className="text-xl font-semibold text-gray-800">Chef's Collection</h3>
                    <p className="text-gray-600">Browse {databaseRecipes.length || '13+'} authentic recipes from renowned Indian chefs!</p>
                    <Button 
                      onClick={() => switchView('database')}
                      className="bg-gradient-to-r from-orange-500 to-red-500 text-white"
                    >
                      Browse Recipes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : !recipes ? (
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Image Upload Section */}
            <Card className="border-2 border-dashed border-purple-300/50 bg-white/90 backdrop-blur-xl card-hover">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  📸 Upload Your Ingredients
                </CardTitle>
                <CardDescription className="text-gray-700 font-medium">
                  Drop your pic and watch AI magic create amazing healthy Indian recipes! 🎨✨
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300 ${
                    dragActive 
                      ? 'border-cyan-400 bg-cyan-400/20 shadow-lg shadow-cyan-400/25' 
                      : 'border-purple-300/50 hover:border-pink-400/70 hover:bg-pink-400/10 hover:shadow-lg hover:shadow-pink-400/20'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {imagePreviews.length > 0 ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="photo-preview-grid-2">
                        {imagePreviews.map((p, i) => (
                          <div key={i} className="relative group">
                            <img
                              src={p.url}
                              alt={`Preview ${i + 1}`}
                              className="w-full h-28 object-cover rounded-lg shadow-md"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(i)}
                              className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-black/80"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-gray-600 font-medium">{selectedImages.length} photo(s) selected • add up to {MAX_IMAGES}</p>
                      <label className="inline-block cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => handleImagesUpload(e.target.files)}
                        />
                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-purple-300 text-purple-700 hover:bg-purple-50 text-sm font-medium">➕ Add more photos</span>
                      </label>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="mx-auto w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl flex items-center justify-center shadow-xl">
                        <Upload className="w-10 h-10 text-white animate-bounce" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-gray-800">
                          Drop your pic here, or <span className="text-purple-600 underline decoration-wavy">browse</span> 📱
                        </p>
                        <p className="text-sm text-gray-600 mt-2 font-medium">
                          JPG, PNG, WEBP up to 10MB • AI-powered ingredient detection 🤖
                        </p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => handleImagesUpload(e.target.files)}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Preferences Section */}
            <Card className="bg-white/90 backdrop-blur-xl card-hover">
              <CardHeader>
                <CardTitle className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-purple-600 bg-clip-text text-transparent">
                  🎯 Your Vibe Check
                </CardTitle>
                <CardDescription className="text-gray-700 font-medium">
                  Tell me your cooking style and let's customize your perfect recipe! 💫
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-800">Meal Type</label>
                    <select 
                      className="flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={preferences.meal_type}
                      onChange={(e) => setPreferences(prev => ({...prev, meal_type: e.target.value}))}
                    >
                      <option value="">Select meal type</option>
                      <option value="breakfast">Breakfast</option>
                      <option value="lunch">Lunch</option>
                      <option value="dinner">Dinner</option>
                      <option value="snack">Snack</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-800">Cooking Expertise</label>
                    <select 
                      className="flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={preferences.cooking_expertise}
                      onChange={(e) => setPreferences(prev => ({...prev, cooking_expertise: e.target.value}))}
                    >
                      <option value="">Select your level</option>
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="expert">Expert</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-800">Cuisine Style</label>
                    <select 
                      className="flex h-10 w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={preferences.cuisine_style}
                      onChange={(e) => setPreferences(prev => ({...prev, cuisine_style: e.target.value}))}
                    >
                      <option value="">Select style</option>
                      <option value="traditional">Traditional Indian</option>
                      <option value="fusion">Modern Fusion</option>
                      <option value="both">Both Styles</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-gray-800">Dietary Preferences</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {['vegetarian', 'gluten-free', 'keto', 'low-carb', 'high-protein', 'dairy-free', 'nut-free'].map((diet) => (
                      <div key={diet} className="flex items-center space-x-2">
                        <Checkbox
                          id={diet}
                          onCheckedChange={(checked) => handleDietaryChange(diet, checked)}
                        />
                        <label htmlFor={diet} className="text-sm text-gray-700 capitalize font-medium">
                          {diet.replace('-', ' ')}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <Button 
                  onClick={analyzeIngredients}
                  disabled={isAnalyzing || selectedImages.length === 0}
                  className="w-full btn-gradient text-white py-4 text-xl font-bold rounded-2xl shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAnalyzing ? (
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                      ✨ AI Magic in Progress...
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <Camera className="w-6 h-6" />
                      🚀 Generate Amazing Recipes
                    </div>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto">
            {recipes.can_cook ? (
              <div className="space-y-6">
                {/* Identified Ingredients */}
                <Card className="bg-white/90 border-teal-300/50 backdrop-blur-xl card-hover">
                  <CardHeader>
                    <CardTitle className="text-2xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent flex items-center gap-2">
                      <Camera className="w-6 h-6 text-teal-600" />
                      🔍 AI Vision Results
                    </CardTitle>
                    <CardDescription className="text-gray-700 font-medium">
                      Check out what our AI spotted in your pic - we're about to create some fire recipes! 🔥
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-3">
                        {recipes.ingredients_identified && recipes.ingredients_identified.length > 0 ? (
                          recipes.ingredients_identified.map((ingredient, index) => (
                            <Badge key={index} className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-4 py-2 text-sm font-bold rounded-full shadow-lg">
                              ✅ {ingredient}
                            </Badge>
                          ))
                        ) : (
                          <Badge className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-4 py-2 rounded-full">
                            📸 AI processing your image...
                          </Badge>
                        )}
                      </div>
                      <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-4 rounded-2xl border border-purple-300">
                        <p className="text-sm text-gray-800 font-medium flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-yellow-600" />
                          <strong className="text-purple-700">Bonus Kitchen Essentials:</strong> <span className="text-gray-700">We're assuming you've got the Indian spice game covered! 🧄🌶️ (turmeric, cumin, coriander, garam masala, onions, tomatoes, ginger-garlic paste, oil)</span>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Database Recipe Matches */}
                {recipes.database_recipes && recipes.database_recipes.length > 0 && (
                  <Card className="bg-white/90 border-orange-300/50 backdrop-blur-xl card-hover">
                    <CardHeader>
                      <CardTitle className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent flex items-center gap-2">
                        <ChefHat className="w-6 h-6 text-orange-600" />
                        👨‍🍳 Chef-Verified Recipes
                      </CardTitle>
                      <CardDescription className="text-gray-700 font-medium">
                        Found {recipes.total_database_matches} authentic recipes from renowned chefs using your ingredients!
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {recipes.database_recipes.slice(0, 4).map((dbRecipe, index) => (
                          <div key={index} className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-xl border border-orange-200">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-bold text-gray-800">{dbRecipe.name}</h4>
                              {dbRecipe.is_featured && (
                                <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs">
                                  ⭐
                                </Badge>
                              )}
                            </div>
                            <div className="space-y-2 text-sm">
                              <p className="text-orange-700">
                                <strong>Chef:</strong> {dbRecipe.chef_name}
                              </p>
                              <p className="text-gray-600">
                                <strong>Region:</strong> {dbRecipe.region} • <strong>Time:</strong> {dbRecipe.cooking_time}
                              </p>
                              {dbRecipe.matched_ingredients && (
                                <p className="text-green-700">
                                  <strong>Matches:</strong> {dbRecipe.matched_ingredients.join(', ')}
                                </p>
                              )}
                              <Button 
                                size="sm"
                                className="w-full mt-2 bg-gradient-to-r from-orange-500 to-red-500 text-white"
                                onClick={() => {
                                  setRecipes({
                                    ...recipes,
                                    main_recipe: {
                                      name: dbRecipe.name,
                                      ingredients: dbRecipe.ingredients,
                                      instructions: dbRecipe.instructions,
                                      cooking_time: dbRecipe.cooking_time,
                                      difficulty: dbRecipe.difficulty,
                                      nutritional_info: dbRecipe.nutritional_info,
                                      chef_tips: dbRecipe.chef_tips
                                    },
                                    chef_message: `This authentic ${dbRecipe.region} recipe is from renowned chef ${dbRecipe.chef_name}!`
                                  });
                                }}
                              >
                                Use This Recipe
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Chef Message */}
                <Card className="bg-white/90 border-pink-300/50 backdrop-blur-xl card-hover">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                        <ChefHat className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent mb-2">
                          👨‍🍳 Chef's Vibe
                        </h3>
                        <p className="text-gray-800 font-medium text-lg italic">{recipes.chef_message}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recipe Tabs */}
                <Tabs value={activeRecipe.toString()} onValueChange={(value) => setActiveRecipe(parseInt(value))}>
                  <TabsList className="grid w-full grid-cols-3 bg-white/90 backdrop-blur-xl rounded-2xl p-2">
                    <TabsTrigger 
                      value="0" 
                      className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-500 data-[state=active]:text-white text-gray-700 font-bold rounded-xl py-3 data-[state=active]:shadow-lg"
                    >
                      🌟 Main Recipe
                    </TabsTrigger>
                    <TabsTrigger 
                      value="1" 
                      className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-blue-500 data-[state=active]:text-white text-gray-700 font-bold rounded-xl py-3 data-[state=active]:shadow-lg"
                    >
                      🎨 Alternative 1
                    </TabsTrigger>
                    <TabsTrigger 
                      value="2" 
                      className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-500 data-[state=active]:to-teal-500 data-[state=active]:text-white text-gray-700 font-bold rounded-xl py-3 data-[state=active]:shadow-lg"
                    >
                      ✨ Alternative 2
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="0" className="mt-6">
                    {renderRecipe(recipes.main_recipe)}
                  </TabsContent>
                  
                  <TabsContent value="1" className="mt-6">
                    {renderRecipe(recipes.alternative_recipes?.[0])}
                  </TabsContent>
                  
                  <TabsContent value="2" className="mt-6">
                    {renderRecipe(recipes.alternative_recipes?.[1])}
                  </TabsContent>
                </Tabs>
              </div>
            ) : (
              <Card className="bg-white/90 border-yellow-300 backdrop-blur-xl">
                <CardContent className="pt-6">
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center mx-auto">
                      <ChefHat className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800">Need More Ingredients!</h3>
                    <p className="text-gray-700 max-w-md mx-auto">{recipes.chef_message}</p>
                    <Button onClick={resetForm} className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white">
                      Try Again
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>

      {/* Authentication Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white/95 backdrop-blur-xl border-gray-200 shadow-2xl">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {authMode === 'login' ? '🔐 Welcome Back!' : '🎉 Join MasterChef!'}
              </CardTitle>
              <CardDescription className="text-gray-700">
                {authMode === 'login' 
                  ? 'Sign in to save your favorite recipes' 
                  : 'Create an account to unlock all features'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAuthSubmit} className="space-y-4">
                {authMode === 'register' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-800">Full Name</label>
                    <input
                      type="text"
                      required
                      className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      value={authForm.full_name}
                      onChange={(e) => setAuthForm(prev => ({...prev, full_name: e.target.value}))}
                      placeholder="Enter your full name"
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-800">Email</label>
                  <input
                    type="email"
                    required
                    className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={authForm.email}
                    onChange={(e) => setAuthForm(prev => ({...prev, email: e.target.value}))}
                    placeholder="Enter your email"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-800">Password</label>
                  <input
                    type="password"
                    required
                    className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    value={authForm.password}
                    onChange={(e) => setAuthForm(prev => ({...prev, password: e.target.value}))}
                    placeholder="Enter your password"
                  />
                </div>
                
                {authMode === 'register' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-800">Phone (Optional)</label>
                    <input
                      type="tel"
                      className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      value={authForm.phone}
                      onChange={(e) => setAuthForm(prev => ({...prev, phone: e.target.value}))}
                      placeholder="Enter your phone number"
                    />
                  </div>
                )}
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowAuthModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="auth-submit-btn"
                    className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                  >
                    {authMode === 'login' ? 'Sign In' : 'Create Account'}
                  </Button>
                </div>
              </form>
              
              <Separator className="my-4" />
              
              <div className="text-center">
                <Button
                  variant="ghost"
                  className="text-purple-600 hover:text-purple-700"
                  onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
                >
                  {authMode === 'login' 
                    ? "Don't have an account? Sign up" 
                    : "Already have an account? Sign in"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default App;